import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { consultationAPI, doctorAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';
import RatingModal from '../../../components/RatingModal';
import { handleIOSPayPerUse, isIOS } from '../../../utils/iapPayment';
import appleIAP, { PRODUCT_IDS } from '../../../services/appleIAP';
import PaymentGateway from '../../../components/PaymentGateway';

const PatientConsultation = () => {
  const { doctorId: doctorIdParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [consultations, setConsultations] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [bookingType, setBookingType] = useState('chat');
  const [symptoms, setSymptoms] = useState('');
  const [reason, setReason] = useState('');

  const [limits, setLimits] = useState({
    chat_remaining: 0, audio_remaining: 0, video_remaining: 0,
    has_subscription: false, pay_per_use_prices: { video: 500, voice: 200, chat: 100 }
  });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [applePrices, setApplePrices] = useState({});
  const [iapFinished, setIapFinished] = useState(false);
  const [showRazorpay, setShowRazorpay] = useState(false);
  const [razorpayBookingData, setRazorpayBookingData] = useState(null);
  const iosDevice = isIOS();

  useEffect(() => {
    if (user?._id) {
      fetchConsultations();
      fetchLimits();
    }
    fetchDoctors();
    if (iosDevice) initIAP();
    else setIapFinished(true);
  }, [user?._id, user?.subscription_status]);

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      const loadPrices = () => {
        const prices = {};
        for (const [type, pid] of [['chat', PRODUCT_IDS.payPerUse.chat], ['voice', PRODUCT_IDS.payPerUse.voice], ['video', PRODUCT_IDS.payPerUse.video]]) {
          const p = appleIAP.getProduct(pid);
          if (p) prices[type] = p.localizedPrice;
        }
        setApplePrices(prices);
      };
      loadPrices();
      
      const removeListener = appleIAP.on('products-updated', loadPrices);
      
      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const hasPrice = appleIAP.getProduct(PRODUCT_IDS.payPerUse.chat)?.localizedPrice;
        if (hasPrice || attempts >= 10) {
          clearInterval(pollInterval);
          loadPrices();
          setIapFinished(true);
          removeListener?.();
        } else {
          loadPrices();
        }
      }, 1000);

      return () => {
        clearInterval(pollInterval);
        removeListener?.();
      };
    } catch (err) {
      console.error('IAP init error:', err);
      setIapFinished(true);
    }
  };

  useEffect(() => {
    if (doctorIdParam && doctors.length > 0) {
      const doctor = doctors.find((d) => d._id === doctorIdParam);
      if (doctor) setSelectedDoctor(doctor);
    }
  }, [doctorIdParam, doctors]);

  const fetchLimits = async () => {
    try {
      const res = await consultationAPI.checkLimits(user._id || user.id);
      if (res.data?.success) {
        setLimits(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch limits', error);
    }
  };

  const fetchConsultations = async () => {
    if (!user?._id && !user?.id) return;
    try {
      const res = await consultationAPI.getHistory(user._id || user.id);
      if (res.data?.success) {
        const list = res.data.data?.items ?? res.data.data;
        setConsultations(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      showError('Failed to fetch consultations');
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await doctorAPI.getApprovedDoctors();
      if (res.data?.success) {
        setDoctors(res.data.data?.approved_doctors || res.data.approved_doctors || []);
      }
    } catch (error) {
      showError('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const [showSafetyPopup, setShowSafetyPopup] = useState(false);
  const [pendingCall, setPendingCall] = useState(null);

  const startConsultation = async (consultationId, type) => {
    if (type === 'chat') {
      navigate(`/chat/${consultationId}`);
    } else {
      const consultation = consultations.find(c => c._id === consultationId);
      setPendingCall({ id: consultationId, type, doctor: consultation?.doctor_id });
      setShowSafetyPopup(true);
    }
  };

  const confirmSafetyCheck = () => {
    if (pendingCall) {
      setShowSafetyPopup(false);
      navigate(`/consultation/${pendingCall.id}`);
      setPendingCall(null);
    }
  };

  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingConsultationId, setRatingConsultationId] = useState(null);

  const openRatingModal = (id) => {
    setRatingConsultationId(id);
    setShowRatingModal(true);
  };

  const getPayPerUsePrice = (type) => {
    // For iOS: use Apple localized price if available
    if (iosDevice && applePrices[type]) return applePrices[type];
    // For web/Android (or iOS fallback): use backend price + GST
    const priceMap = limits?.pay_per_use_prices || { video: 500, voice: 200, chat: 100 };
    const base = priceMap[type] || (type === 'video' ? 500 : type === 'voice' ? 200 : 100);
    return `₹${Math.round(base * 1.18)}`;
  };

  // Keep legacy name for UI compatibility
  const getPriceText = (type) => getPayPerUsePrice(type);

  const getIAPProductId = (type) => {
    if (type === 'video') return PRODUCT_IDS.payPerUse.video;
    if (type === 'voice') return PRODUCT_IDS.payPerUse.voice;
    return PRODUCT_IDS.payPerUse.chat;
  };

  const getPayPerUseAmount = (type) => {
    const priceMap = limits?.pay_per_use_prices || { video: 500, voice: 200, chat: 100 };
    return priceMap[type] || (type === 'video' ? 500 : type === 'voice' ? 200 : 100);
  };

  const hasFreeSlot = () => {
    if (!limits.has_subscription) return false;
    const key = bookingType === 'voice' ? 'audio_remaining' : `${bookingType}_remaining`;
    return limits[key] > 0;
  };

  const bookConsultation = async () => {
    if (!selectedDoctor) {
      showError('Please select a doctor');
      return;
    }
    const consultationType = bookingType === 'voice' ? 'audio' : bookingType;

    if (hasFreeSlot()) {
      try {
        const res = await consultationAPI.request({
          doctor_id: selectedDoctor._id, consultation_type: consultationType,
          symptoms, reason_for_visit: reason
        });
        if (res.data?.success) {
          showSuccess('Consultation booked successfully');
          fetchConsultations();
          fetchLimits();
        } else {
          showError(res.data?.message || 'Failed to book consultation');
        }
      } catch (error) {
        showError(error.response?.data?.message || 'Failed to book consultation');
      }
      return;
    }

    // iOS: use Apple IAP
    if (iosDevice) {
      if (!applePrices[bookingType] && !iapFinished) {
        showError('Price is loading, please wait a moment.');
        return;
      }
      setProcessingPayment(true);
      const productId = getIAPProductId(bookingType);
      await handleIOSPayPerUse({
        productId,
        onSuccess: async () => {
          try {
            const res = await consultationAPI.request({
              doctor_id: selectedDoctor._id, consultation_type: consultationType,
              symptoms, reason_for_visit: reason,
              payment_method: 'apple_iap'
            });
            if (res.data?.success) {
              showSuccess('Consultation booked successfully');
              fetchConsultations();
              fetchLimits();
            } else {
              showError(res.data?.message || 'Failed to book consultation');
            }
          } catch (error) {
            showError(error.response?.data?.message || 'Booking failed');
          }
        },
        onError: (msg) => showError(msg),
        onFinally: () => setProcessingPayment(false),
      });
      return;
    }

    // Web/Android: use Razorpay
    setRazorpayBookingData({ doctor_id: selectedDoctor._id, consultation_type: consultationType, symptoms, reason_for_visit: reason });
    setShowRazorpay(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10 py-8 px-4">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-yellow-400 to-amber-500">
          My Consultations
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Connect with our top-rated specialists directly via Chat, Voice, or Video.
        </p>
      </div>

      <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl p-8 border border-white/5 shadow-2xl backdrop-blur-xl">
        <h2 className="text-2xl font-bold text-white mb-6 tracking-wide">Book New Consultation</h2>
        <div className="space-y-6">
          <div className="group">
            <label className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider group-hover:text-yellow-500 transition-colors">Select Doctor</label>
            <div className="relative">
              <select
                value={selectedDoctor?._id || ''}
                onChange={(e) => {
                  const doctor = doctors.find(d => d._id === e.target.value);
                  setSelectedDoctor(doctor);
                }}
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-5 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent transition-all backdrop-blur-md"
              >
                <option value="" className="bg-gray-900">Select a specialist</option>
                {doctors.map((doctor) => (
                  <option key={doctor._id} value={doctor._id} className="bg-gray-900">
                    Dr. {doctor.name} — {doctor.specialization}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group">
              <label className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider group-hover:text-yellow-500 transition-colors">Reason for Visit</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Regular checkup, Fever"
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all backdrop-blur-md placeholder-gray-600"
              />
            </div>
            <div className="group">
              <label className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider group-hover:text-yellow-500 transition-colors">Symptoms (Optional)</label>
              <input
                type="text"
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                placeholder="Describe your symptoms..."
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-5 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all backdrop-blur-md placeholder-gray-600"
              />
            </div>
          </div>

          <div className="pt-4">
            <label className="block text-sm font-medium text-gray-400 mb-4 uppercase tracking-wider">
              Consultation Mode
              {limits.has_subscription && <span className="text-yellow-500 ml-2 font-normal normal-case">(Using Subscription Allowances)</span>}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'chat', label: 'Chat Session', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', remaining: limits?.chat_remaining || 0 },
                { value: 'voice', label: 'Voice Call', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', remaining: limits?.audio_remaining || 0 },
                { value: 'video', label: 'Video Call', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', remaining: limits?.video_remaining || 0 }
              ].map(({ value, label, icon, remaining }) => {
                const isSelected = bookingType === value;
                const hasSlot = limits.has_subscription && remaining > 0;
                return (
                  <button
                    key={value}
                    onClick={() => setBookingType(value)}
                    className={`relative overflow-hidden group p-5 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 border ${
                      isSelected
                        ? 'bg-yellow-500/10 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)]'
                        : 'bg-white/5 border-white/5 hover:border-white/20 hover:bg-white/10'
                    }`}
                  >
                    {isSelected && <div className="absolute inset-0 bg-gradient-to-t from-red-600/10 to-transparent"></div>}
                    <svg className={`w-8 h-8 mb-3 transition-colors ${isSelected ? 'text-yellow-500' : 'text-gray-400 group-hover:text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={icon}></path>
                    </svg>
                    <span className={`font-semibold mb-1 ${isSelected ? 'text-white' : 'text-gray-300'}`}>{label}</span>
                    <span className={`text-sm font-medium ${hasSlot ? 'text-green-400' : isSelected ? 'text-yellow-500' : 'text-gray-500'}`}>
                      {limits.has_subscription
                        ? (hasSlot ? `${remaining} remaining` : 'Limit reached')
                        : getPriceText(value)
                      }
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-6 border-t border-white/10">
            <button
              onClick={bookConsultation}
              disabled={!selectedDoctor || processingPayment || (iosDevice && !hasFreeSlot() && !applePrices[bookingType] && !iapFinished)}
              className={`w-full py-5 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl ${
                !selectedDoctor || processingPayment
                  ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/5'
                  : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(234,179,8,0.4)] active:translate-y-0'
              }`}
            >
              {processingPayment
                ? 'Processing...'
                : hasFreeSlot()
                  ? 'Book Free Session'
                  : `Purchase & Book (${getPriceText(bookingType)})`
              }
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white/5 rounded-2xl p-8 border border-white/10 backdrop-blur-xl">
        <h2 className="text-2xl font-bold text-white mb-6">Recent Consultations</h2>
        <div className="space-y-4">
          {consultations.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
              <p className="text-gray-400">No consultations yet.</p>
              <p className="text-sm text-gray-600 mt-2">Your history will appear here once you book a session.</p>
            </div>
          ) : (
            consultations.map((consultation) => {
              const scheduledTime = consultation.scheduledAt ? new Date(consultation.scheduledAt).getTime() : 0;
              const now = Date.now();
              const isScheduled = consultation.status === 'scheduled';
              const canJoin = isScheduled ? now >= scheduledTime : true;
              const showSupport = isScheduled && now > (scheduledTime + 30 * 60 * 1000);

              return (
                <div key={consultation._id} className="bg-black/30 rounded-xl p-5 border border-white/5 hover:border-white/10 transition-colors">
                  <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Dr. {consultation.doctor_id?.name || 'Specialist'}</h3>
                        <p className="text-sm text-yellow-500 capitalize">{consultation.consultation_type} Session</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:flex-col md:items-end gap-2">
                      <span className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                        consultation.status === 'completed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                        consultation.status === 'in_progress' || consultation.status === 'active' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse' :
                        consultation.status === 'scheduled' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                        'bg-amber-600/20 text-yellow-400 border border-red-700/30'
                      }`}>
                        {consultation.status === 'active' ? 'IN PROGRESS' : consultation.status.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500 font-medium">{new Date(consultation.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {isScheduled && (
                    <div className="bg-purple-900/20 border border-purple-500/20 p-4 rounded-lg mb-4">
                      <p className="text-purple-300 text-sm font-semibold flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        Scheduled: {new Date(scheduledTime).toLocaleString()}
                      </p>
                      {!canJoin && <p className="text-xs text-purple-400/60 mt-1 ml-6">Join button activates at scheduled time.</p>}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 justify-end mt-4">
                    {consultation.status === 'completed' && !consultation.is_rated && (
                      <button
                        onClick={() => openRatingModal(consultation._id)}
                        className="px-5 py-2 rounded-lg font-medium text-sm bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors"
                      >
                        Rate Doctor
                      </button>
                    )}

                    {showSupport && (
                      <button
                        onClick={() => alert('Support: Please call 9606030503')}
                        className="px-5 py-2 rounded-lg font-medium text-sm bg-yellow-400/10 hover:bg-yellow-400/20 text-yellow-400 border border-yellow-400/20 transition-colors"
                      >
                        Contact Support
                      </button>
                    )}

                    {(consultation.status === 'scheduled' || consultation.status === 'active' || consultation.status === 'in_progress') && (
                      <button
                        onClick={() => startConsultation(consultation._id, consultation.consultation_type)}
                        disabled={isScheduled && !canJoin}
                        className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all shadow-lg ${
                          isScheduled && !canJoin
                            ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/5'
                            : 'bg-yellow-500 text-black hover:bg-yellow-400 hover:-translate-y-0.5'
                        }`}
                      >
                        {consultation.consultation_type === 'chat'
                          ? (consultation.status === 'active' || consultation.status === 'in_progress' ? 'Resume Chat' : 'Open Chat')
                          : (consultation.status === 'active' || consultation.status === 'in_progress' ? 'Re-Join Call' : 'Join Call')
                        }
                      </button>
                    )}

                    {consultation.status === 'completed' && (
                      <button
                        onClick={() => navigate(`/patient/prescription/${consultation._id}`)}
                        className="px-5 py-2 rounded-lg font-medium text-sm bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 transition-colors"
                      >
                        View Prescription
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showSafetyPopup && pendingCall && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full p-8 border border-yellow-500/30 shadow-2xl text-center relative">
            <h3 className="text-2xl font-black text-yellow-500 mb-6 flex items-center justify-center gap-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              Safety Verification
            </h3>
            <div className="mb-8 flex flex-col items-center">
              <div className="w-32 h-32 rounded-full border-4 border-yellow-500/50 shadow-[0_0_20px_rgba(234,179,8,0.2)] overflow-hidden mb-4 bg-gray-800">
                {pendingCall.doctor?.profile_image ? (
                  <img
                    src={formatImageUrl(pendingCall.doctor.profile_image)}
                    alt="Doctor"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">No Image</div>
                )}
              </div>
              <p className="text-white text-lg font-bold">Dr. {pendingCall.doctor?.name}</p>
              <p className="text-yellow-500/80 text-sm">Verify Identity on Call</p>
            </div>
            <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-4 mb-8 text-left">
              <p className="text-yellow-400 text-sm leading-relaxed">
                <strong className="block mb-1">Strict Safety Protocol:</strong>
                Please verify that the person on the video call matches the photo above. If they do not match, <strong>disconnect immediately</strong> and report the issue to our support team.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={confirmSafetyCheck}
                className="bg-yellow-500 text-black px-6 py-4 rounded-xl font-bold hover:bg-yellow-400 transition-colors w-full shadow-lg"
              >
                I Understand & Continue
              </button>
              <button
                onClick={() => setShowSafetyPopup(false)}
                className="py-3 text-gray-400 hover:text-white font-medium transition-colors"
              >
                Cancel Call
              </button>
            </div>
          </div>
        </div>
      )}

      {showRatingModal && ratingConsultationId && (
        <RatingModal
          consultationId={ratingConsultationId}
          onClose={() => setShowRatingModal(false)}
          onSuccess={() => fetchConsultations()}
        />
      )}

      {showRazorpay && razorpayBookingData && (
        <PaymentGateway
          amount={getPayPerUseAmount(bookingType)}
          planName={`${bookingType.charAt(0).toUpperCase() + bookingType.slice(1)} Consultation`}
          userId={user?._id || user?.id}
          productId={`pay_per_use_${bookingType}`}
          role="patient"
          onSuccess={async () => {
            try {
              const res = await consultationAPI.request({
                ...razorpayBookingData,
                payment_method: 'razorpay'
              });
              if (res.data?.success) {
                showSuccess('Consultation booked successfully!');
                fetchConsultations();
                fetchLimits();
              } else {
                showError(res.data?.message || 'Failed to book consultation');
              }
            } catch (error) {
              showError(error.response?.data?.message || 'Booking failed');
            } finally {
              setShowRazorpay(false);
              setRazorpayBookingData(null);
            }
          }}
          onError={(msg) => {
            showError(msg);
            setShowRazorpay(false);
            setRazorpayBookingData(null);
          }}
          onClose={() => {
            setShowRazorpay(false);
            setRazorpayBookingData(null);
          }}
        />
      )}
    </div>
  );
};

export default PatientConsultation;
